import moment from 'moment-timezone';
import { DATE_FORMAT } from '../constants';

export const formattedDayOfTheWeekDayMonthShipmentDate = value =>
  value ? moment(value).format(DATE_FORMAT.DAY_OF_WEEK_DAY_MONTH_FORMAT) : '';

export const getUKCurrentMomentTime = () =>
  moment().tz(DATE_FORMAT.EUROPE_LONDON_TIME_ZONE);

export const getUkTodayIsoDate = () =>
  getUKCurrentMomentTime().format(DATE_FORMAT.YYYY_MM_DD_FORMAT);

export const clampIsoDateToMin = (value, minIso) => {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v || !minIso) {
    return value;
  }
  return v < minIso ? minIso : v;
};

export const addCalendarDaysToIsoDate = (isoDate, dayCount) => {
  if (!isoDate || typeof dayCount !== 'number') {
    return '';
  }
  const m = moment(isoDate, DATE_FORMAT.YYYY_MM_DD_FORMAT, true);
  if (!m.isValid()) {
    return '';
  }
  return m.add(dayCount, 'days').format(DATE_FORMAT.YYYY_MM_DD_FORMAT);
};

export const clampIsoDateToRange = (value, minIso, maxIso) => {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v) {
    return v;
  }
  let out = v;
  if (minIso && out < minIso) {
    out = minIso;
  }
  if (maxIso && out > maxIso) {
    out = maxIso;
  }
  return out;
};

const MINIMUM_COLLECTION_LEAD_MINUTES = 60;

export function getUkPrebookTimeReadyDefaultsOneHourFromNow() {
  const m = getUKCurrentMomentTime().add(
    MINIMUM_COLLECTION_LEAD_MINUTES,
    'minutes'
  );
  return {
    hour: m.format('HH'),
    minute: m.format('mm'),
  };
}

export function isUkCollectionScheduleInPast(
  collectionDateIso,
  timeHour,
  timeMinute
) {
  const d = String(collectionDateIso ?? '').trim();
  if (!d || timeHour == null || timeMinute == null) {
    return false;
  }
  const hh = String(timeHour).padStart(2, '0');
  const mm = String(timeMinute).padStart(2, '0');
  const parsed = moment.tz(
    `${d} ${hh}:${mm}:00`,
    `${DATE_FORMAT.YYYY_MM_DD_FORMAT} HH:mm:ss`,
    DATE_FORMAT.EUROPE_LONDON_TIME_ZONE
  );
  if (!parsed.isValid()) {
    return false;
  }
  const minimumStart = getUKCurrentMomentTime()
    .add(MINIMUM_COLLECTION_LEAD_MINUTES, 'minutes')
    .startOf('minute');
  return parsed.isBefore(minimumStart);
}

export function formatUkPrebookCollectionScheduleLabel(
  collectionDateIso,
  timeHour,
  timeMinute
) {
  const d = String(collectionDateIso ?? '').trim();
  const hh = timeHour != null ? String(timeHour).padStart(2, '0') : '';
  const mm = timeMinute != null ? String(timeMinute).padStart(2, '0') : '';

  if (!d || !hh || !mm) {
    return null;
  }

  const parsed = moment.tz(
    `${d} ${hh}:${mm}:00`,
    `${DATE_FORMAT.YYYY_MM_DD_FORMAT} HH:mm:ss`,
    DATE_FORMAT.EUROPE_LONDON_TIME_ZONE
  );

  if (!parsed.isValid()) {
    return null;
  }

  return `${parsed.format('dddd, D MMMM YYYY')} - ready at ${hh}:${mm}`;
}

export const getNextWorkingDay = (date = getUKCurrentMomentTime()) => {
  let nextDay = moment(date).add(1, 'days');

  while (nextDay.isoWeekday() > 5) {
    nextDay.add(1, 'days');
  }

  return nextDay;
};

export const getNextSaturday = (date = moment()) => {
  let nextDay = moment(date).add(1, 'days');

  while (nextDay.isoWeekday() !== 6) {
    nextDay.add(1, 'days');
  }

  return nextDay;
};

export const getNextSunday = (date = moment()) => {
  let nextDay = moment(date).add(1, 'days');

  while (nextDay.isoWeekday() !== 7) {
    nextDay.add(1, 'days');
  }

  return nextDay;
};

export const isFriOrSat = (date = moment()) => {
  let today = moment(date);
  return today.isoWeekday() === 5 || today.isoWeekday() === 6;
};

export const addBusinessDays = (date, days) => {
  if (!date || !days) {
    return null;
  }
  let result = moment(date);
  while (days > 0) {
    result.add(1, 'days');
    if (result.day() !== 0 && result.day() !== 6) {
      days--;
    }
  }
  return result;
};

export const getDayAndMonth = date =>
  moment(date).isValid()
    ? `${moment(date).format(DATE_FORMAT.DISPLAY_DATE_TIME_FORMAT)}`
    : '';

export const isPresentOrFutureDate = date => {
  if (!date) {
    return false;
  }

  const inputDate = moment(date, DATE_FORMAT.YYYY_MM_DD_FORMAT, true);

  if (!inputDate.isValid()) {
    return false;
  }

  const currentDate = moment().startOf('day');

  return inputDate.isSameOrAfter(currentDate, 'day');
};

export const convertToInternalDate = localDate => {
  if (!localDate) {
    return null;
  }
  const dateInternal = new Date('1967-12-31');

  const targetDate = new Date(localDate);

  if (isNaN(targetDate)) {
    return null;
  }

  const msInDay = 24 * 60 * 60 * 1000;
  const daysDifference = Math.floor((targetDate - dateInternal) / msInDay);

  return daysDifference;
};

export const isValidGetLabelDate = (date, expirationDiff = 14) => {
  if (!date) {
    return false;
  }

  const inputDate = moment(date, DATE_FORMAT.YYYY_MM_DD_FORMAT, true);

  if (!inputDate.isValid()) {
    return false;
  }

  const now = moment();

  if (now.diff(inputDate, 'days') > expirationDiff) {
    return false;
  }

  return true;
};
